import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http'
import { Store } from '@ngrx/store'
import { BehaviorSubject, Observable, throwError } from 'rxjs'
import {
  catchError,
  filter,
  finalize,
  switchMap,
  take,
  mergeMap
} from 'rxjs/operators'

import * as fromAuthActions from './auth.actions'
import { AuthServiceGeneric } from './auth.service'
import { Injectable, Injector } from '@angular/core'
import { Router } from '@angular/router'
import { environment } from '../../../environments/environment'

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private AUTH_HEADER = 'Authorization'
  private refreshTokenInProgress = false
  private refreshTokenSubject = new BehaviorSubject<string | null>(null)
  private _store: Store

  constructor(private injector: Injector, private router: Router) {}

  private get store(): Store {
    if (!this._store) {
      this._store = this.injector.get(Store)
    }
    return this._store
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip authentication for public endpoints
    if (this.isPublicRequest(req)) {
      return next.handle(req)
    }

    const authService = this.injector.get(AuthServiceGeneric)

    return authService.isLoggedIn().pipe(
      take(1),
      switchMap((loggedIn) => {
        if (!loggedIn) {
          // User is not logged in - redirect to login and return error
          this.router.navigate(['/login'])
          return throwError(
            () =>
              new HttpErrorResponse({
                error: 'Not authenticated',
                status: 401,
                statusText: 'Unauthorized'
              })
          )
        }

        // User is logged in - get access token
        return authService.getAccessTokenSilently().pipe(
          take(1),
          catchError((err) => {
            console.error('❌ Token acquisition failed:', err)
            localStorage.removeItem('ANMS-AUTH')
            this.router.navigate(['/login'])
            return throwError(
              () =>
                new HttpErrorResponse({
                  error: 'Token acquisition failed',
                  status: 401,
                  statusText: 'Unauthorized',
                  url: req.url
                })
            )
          })
        )
      }),
      mergeMap((token) => {
        // Add token to request and proceed
        const authReq = this.addTokenToRequest(req, token)
        return next.handle(authReq).pipe(
          catchError((error) => {
            // Handle 401 errors from the backend
            if (this.isUnauthorizedError(error)) {
              return this.handle401Error(req, next)
            }
            return throwError(() => error)
          })
        )
      })
    )
  }

  private isPublicRequest(req: HttpRequest<any>): boolean {
    const publicUrls = [
      '/api/v1/login',
      '/api/v1/register',
      '/api/v1/logout',
      '/api/v1/management/sendVerificationEmail',
      '/callback',
      '/login',
      '/assets/',
      '/i18n/',
      '/register',
      'api.openai.com/v1/models'
    ]
    const isLocalPublic = publicUrls.some((url) => req.url.includes(url))

    const isAuth0Request =
      req.url.includes(`https://${environment.auth0.domain}`) ||
      req.url.includes('/oauth/token') ||
      req.url.includes('/.well-known/openid-configuration')

    return isLocalPublic || isAuth0Request
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const authService = this.injector.get(AuthServiceGeneric)

    if (!this.refreshTokenInProgress) {
      // First request to fail with 401 - attempt token refresh
      this.refreshTokenInProgress = true
      this.refreshTokenSubject.next(null)

      return authService.refreshAccessToken().pipe(
        switchMap((newToken) => {
          // Token refresh successful - notify waiting requests
          this.refreshTokenSubject.next(newToken)
          return next.handle(this.addTokenToRequest(request, newToken))
        }),
        finalize(() => {
          this.refreshTokenInProgress = false
        }),
        catchError((error) => {
          // Token refresh failed - logout user
          console.error('❌ Token refresh failed:', error)
          this.store.dispatch(fromAuthActions.authLogout())
          this.router.navigate(['/login'])
          return throwError(() => error)
        })
      )
    } else {
      // Token refresh already in progress - wait for it to complete
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) =>
          next.handle(this.addTokenToRequest(request, token!))
        )
      )
    }
  }

  private addTokenToRequest(
    request: HttpRequest<any>,
    token: string | null
  ): HttpRequest<any> {
    return token
      ? request.clone({
          headers: request.headers.set(this.AUTH_HEADER, `Bearer ${token}`)
        })
      : request
  }

  private isUnauthorizedError(error: any): boolean {
    return error instanceof HttpErrorResponse && error.status === 401
  }
}
